"use client";
import { useEffect, useRef, type ReactNode } from "react";
import { type Translationtype } from "utils";

interface IConfirmProps {
  title: string;
  isOpened: boolean;
  setIsOpened?: (isOpen: boolean) => void;
  handleOk: VoidFunction;
  handleSecondOk?: VoidFunction;
  handleClose: VoidFunction;
  okBtnText?: string;
  secondOkBtnText?: string;
  cancelBtnText?: string;
  okBtnClassName?: string;
  cancelBtnClassName?: string;
  hideOkBtn?: boolean;
  hideSecondOkBtn?: boolean;
  children?: ReactNode;
  maxWidth?: string;
  t: Translationtype;
}

export function Confirm({
  title,
  isOpened: open,
  setIsOpened,
  handleOk,
  handleSecondOk,
  handleClose,
  okBtnText,
  secondOkBtnText,
  cancelBtnText,
  okBtnClassName = "btn btn-icon",
  cancelBtnClassName = "btn btn-outline btn-default",
  children,
  hideOkBtn = false,
  hideSecondOkBtn = true,
  maxWidth,
  t,
}: IConfirmProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    // Do not delete this. This triggers the native (default) backdrop layer
    if (open) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }

    const handleCancel = (event: Event) => {
      event.preventDefault();
      handleClose();
    };

    dialog?.addEventListener("cancel", handleCancel);

    return () => {
      dialog?.removeEventListener("cancel", handleCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <dialog className="modal" ref={ref} onClose={() => setIsOpened?.(false)}>
      <div
        className={`modal-box ${maxWidth || "max-w-2xl"} w-[calc(100%-20px)] md:w-full`}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>

        {children ? (
          <div className="modal-content relative z-20 my-4">{children}</div>
        ) : null}

        <div className="modal-footer flex justify-end gap-2">
          {!hideOkBtn ? (
            <button
              className={okBtnClassName}
              type="button"
              onClick={() => handleOk()}
            >
              {okBtnText || t("Buttons.ok")}
            </button>
          ) : null}
          {!hideSecondOkBtn ? (
            <button
              className={okBtnClassName}
              type="button"
              onClick={() => (handleSecondOk ? handleSecondOk() : undefined)}
            >
              {secondOkBtnText || t("Buttons.setNameAll")}
            </button>
          ) : null}

          <button
            className={cancelBtnClassName}
            type="button"
            onClick={() => handleClose()}
          >
            {cancelBtnText || t("Buttons.cancel")}
          </button>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop">
        <button type="button" onClick={() => handleClose()}>
          close
        </button>
      </form>
    </dialog>
  );
}
